import React, { useEffect } from "react";
import { Input, Space, Radio } from "antd";
import IntlMessages from "@iso/components/utility/intlMessages";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  getTransactions,
  getTransactionsById,
} from "../../services/transactions/api";
import { createNotification } from "../../components";
import TransactionsTable from "../TransactionsTable/TransactionsTable";
import PageHeader from "../../components/utility/pageHeader";
import { useAuth } from "../../contexts/auth";

export default function Transactions() {
  const intl = useIntl();

  const { signed, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [environment, setEnvironment] = React.useState(
    localStorage.getItem("environment") || "S",
  );

  const [dataList, setDataList] = React.useState({
    data: [],
    loading: true,
  });

  useEffect(() => {
    if (!signed) {
      navigate("/");
    }
    localStorage.setItem("environment", environment);
    loadAllTransactions()
  }, [environment]);


  async function loadAllTransactions() {
    setDataList({
      data: [], loading: true
    })
    await getTransactions(environment, currentWorkspace)
      .then((response) => {
        setDataList({
          data: response.data, loading: false
        })
      }).catch(() => {
        createNotification('error', 'Fail to load transactions')
        setDataList({
          data: [], loading: false
        })
      })
  }

  async function searchTransaction(value, e) {
    e.preventDefault();

    if (!value) {
      loadAllTransactions()
      return;
    }

    await getTransactionsById(value.trim())
      .then((response) => {
        if (response.status && response.status === 404) {
          createNotification('error', 'Transaction not found.')
        } else {
          navigate(`/transactions/${response.data.ext_trans_id}`)
        }
      }).catch(() => {
        createNotification('error', 'Error searching transaction.')
      })
  }


  const renderEnv = () => {
    if (environment === "S") {
      return "Sandbox";
    }
    return <IntlMessages id="page.transactions.environments.production" />;
  };

  return (
    <>
      <PageHeader>
        <IntlMessages id="page.transactions.title" />
        <span>
          <IntlMessages id="page.transactions.subtitle" />
        </span>
      </PageHeader>
      <div className="py-5 px-7 rounded-xl bg-white border-1 shadow-sm">
        <div className="flex py-2">
          <p>
            <IntlMessages id="page.transactions.environments" />
          </p>
        </div>
        <div className="grid lg:grid-cols-2 grid-cols-1 gap-5 justify-between items-center mb-2">
          <form>
            <div className="flex items-center">
              <Radio.Group
                value={environment}
                size="large"
                buttonStyle="solid"
                onChange={(e) => {
                  setEnvironment(e.target.value)
                }}
                className="rounded-full"
              >
                <Radio.Button value="S">Sandbox</Radio.Button>
                <Radio.Button value="P">
                  <IntlMessages id="page.transactions.environments.production" />
                </Radio.Button>
              </Radio.Group>
              <div
                className="ml-3 cursor-pointer"
                onClick={() => {
                  loadAllTransactions()
                }}
              >
                <i>
                  <svg
                    width="23"
                    height="23"
                    viewBox="0 0 23 23"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11.5161 21.0285C13.8427 21.0355 16.092 20.1975 17.843 18.6716C19.5941 17.1456 20.7268 15.0363 21.0292 12.7384C21.1002 12.1986 21.5971 11.8184 22.139 11.8892C22.6808 11.9599 23.0625 12.4548 22.9915 12.9946C22.6266 15.7679 21.2595 18.3137 19.1461 20.1553C17.0328 21.997 14.3181 23.0083 11.5102 22.9999C8.70231 22.9916 5.99372 21.9641 3.89148 20.1099C1.78924 18.2557 0.437453 15.7018 0.0891771 12.9264C-0.259101 10.151 0.420001 7.34424 1.99937 5.0316C3.57873 2.71897 5.95008 1.05898 8.66951 0.362374C11.3889 -0.334228 14.27 -0.0196903 16.7734 1.24711C18.4849 2.11318 19.9397 3.38375 21.0208 4.93127L21.0208 1.03795C21.0208 0.493553 21.4638 0.0522345 22.0103 0.0522346C22.5569 0.0522346 22.9999 0.493553 22.9999 1.03795L22.9999 7.60937C22.9999 8.15377 22.5569 8.59509 22.0103 8.59509L21.3737 8.59509C21.3585 8.59544 21.3433 8.59543 21.3282 8.59509L15.4134 8.59509C14.8669 8.59509 14.4238 8.15377 14.4238 7.60937C14.4238 7.06498 14.8669 6.62366 15.4134 6.62366L19.7628 6.62366C18.8424 5.0844 17.4988 3.82541 15.8771 3.00475C13.8028 1.95512 11.4156 1.6945 9.1624 2.27168C6.90916 2.84887 4.94433 4.22429 3.63571 6.14047C2.3271 8.05665 1.76441 10.3822 2.05298 12.6819C2.34156 14.9815 3.46161 17.0976 5.20346 18.6339C6.94532 20.1703 9.18958 21.0216 11.5161 21.0285Z"
                      fill="#6A6A6A"
                    />
                  </svg>
                </i>
              </div>
            </div>
          </form>
          <form className="flex lg:justify-end">
            <Space.Compact>
              <Input.Search
                id="search"
                size="large"
                allowClear
                onSearch={searchTransaction}
                placeholder={intl.formatMessage({ id: "page.transactions.search" })}
              />
            </Space.Compact>
          </form>
          <div className="flex">
            <small style={{ fontSize: 13, overflow: "hidden" }}>
              <IntlMessages id="page.transactions.environments.label" />{" "}
              {renderEnv()}
            </small>
          </div>
        </div>
        <TransactionsTable dataList={dataList} />
      </div>
    </>
  );
}
